import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if session should be cleared (non-remembered session + browser was closed)
    const rememberMe = localStorage.getItem("gerio-remember-me");
    const sessionActive = sessionStorage.getItem("gerio-session-active");
    const sessionExpiry = localStorage.getItem("gerio-session-expiry");

    const shouldClearSession =
      (rememberMe === "false" && !sessionActive) ||
      (sessionExpiry && Date.now() > Number(sessionExpiry));

    if (shouldClearSession) {
      supabase.auth.signOut().then(() => {
        localStorage.removeItem("gerio-remember-me");
        localStorage.removeItem("gerio-session-expiry");
        setLoading(false);
      });
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (event === "SIGNED_IN") {
          document.cookie = "gerio_logged_in=1; domain=.gerio.pt; path=/; max-age=2592000; secure; samesite=lax";
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    document.cookie = "gerio_logged_in=; domain=.gerio.pt; path=/; max-age=0";
    localStorage.removeItem("gerio-remember-me");
    localStorage.removeItem("gerio-session-expiry");
    sessionStorage.removeItem("gerio-session-active");
    setUser(null);
    setSession(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
